/**
 * ID Generator Service
 *
 * Generates unique IDs for nodes and edges in workflows.
 */

/**
 * Generate a unique ID with optional prefix
 */
export function generateId(prefix = ''): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 9);
  return prefix ? `${prefix}-${timestamp}${randomPart}` : `${timestamp}${randomPart}`;
}

/**
 * Generate a node ID based on type
 */
export function generateNodeId(nodeType: string, index?: number): string {
  const baseId = nodeType.replace(/Node$/, '');
  const suffix = index !== undefined ? `-${index}` : `-${generateShortId()}`;
  return `${baseId}${suffix}`;
}

/**
 * Generate an edge ID from source and target
 */
export function generateEdgeId(source: string, target: string, handle?: string): string {
  const handleSuffix = handle ? `-${handle}` : '';
  return `edge-${source}-${target}${handleSuffix}`;
}

/**
 * Generate a short unique ID
 */
export function generateShortId(): string {
  return Math.random().toString(36).substring(2, 8);
}

/**
 * Class for generating sequential IDs
 */
export class SequentialIdGenerator {
  private counters: Map<string, number> = new Map();

  /**
   * Generate next ID for a given prefix
   */
  next(prefix: string): string {
    const current = this.counters.get(prefix) || 0;
    this.counters.set(prefix, current + 1);
    return `${prefix}-${current + 1}`;
  }

  /**
   * Reset counters
   */
  reset(): void {
    this.counters.clear();
  }

  /**
   * Get current count for a prefix
   */
  getCount(prefix: string): number {
    return this.counters.get(prefix) || 0;
  }
}

/**
 * Ensure all nodes have unique IDs
 * If duplicate IDs are found, append suffix to make unique
 */
export function ensureUniqueNodeIds<T extends { id: string }>(
  nodes: T[]
): T[] {
  const seenIds = new Set<string>();

  return nodes.map((node) => {
    let id = node.id;
    let counter = 1;

    while (seenIds.has(id)) {
      id = `${node.id}_${counter}`;
      counter++;
    }

    seenIds.add(id);

    if (id !== node.id) {
      return { ...node, id };
    }

    return node;
  });
}

/**
 * Ensure all edges have unique IDs and valid references
 */
export function ensureUniqueEdgeIds<T extends { id: string; source: string; target: string }>(
  edges: T[],
  validNodeIds: Set<string>
): T[] {
  const seenIds = new Set<string>();

  return edges
    .filter((edge) => {
      // Filter out edges with invalid references
      return validNodeIds.has(edge.source) && validNodeIds.has(edge.target);
    })
    .map((edge) => {
      let id = edge.id;
      let counter = 1;

      while (seenIds.has(id)) {
        id = `${edge.id}_${counter}`;
        counter++;
      }

      seenIds.add(id);

      if (id !== edge.id) {
        return { ...edge, id };
      }

      return edge;
    });
}

/**
 * Generate IDs for nodes that don't have them
 */
export function assignMissingNodeIds<T extends { id?: string; type?: string }>(
  nodes: T[]
): (T & { id: string })[] {
  const generator = new SequentialIdGenerator();
  const existingIds = new Set(nodes.filter((n) => n.id).map((n) => n.id as string));

  return nodes.map((node) => {
    if (node.id) {
      return node as T & { id: string };
    }

    const prefix = node.type || 'node';
    let newId = generator.next(prefix);

    // Ensure the generated ID doesn't conflict with existing IDs
    while (existingIds.has(newId)) {
      newId = generator.next(prefix);
    }

    existingIds.add(newId);

    return { ...node, id: newId };
  });
}

/**
 * Generate IDs for edges that don't have them
 */
export function assignMissingEdgeIds<
  T extends { id?: string; source: string; target: string; sourceHandle?: string }
>(edges: T[]): (T & { id: string })[] {
  const existingIds = new Set(edges.filter((e) => e.id).map((e) => e.id as string));

  return edges.map((edge) => {
    if (edge.id) {
      return edge as T & { id: string };
    }

    let newId = generateEdgeId(edge.source, edge.target, edge.sourceHandle);
    let counter = 1;

    // Ensure the generated ID doesn't conflict with existing IDs
    while (existingIds.has(newId)) {
      newId = `${generateEdgeId(edge.source, edge.target, edge.sourceHandle)}_${counter}`;
      counter++;
    }

    existingIds.add(newId);

    return { ...edge, id: newId };
  });
}
